/*
 * Arctic Spas Referral Program — front-end logic
 *
 * Used by:
 *   - page.referral.liquid           (signup landing page)
 *   - page.referral-dashboard.liquid (referrer dashboard with magic-link auth)
 *   - snippets/referral-tracker.liquid (site-wide cookie tracker)
 *
 * Talks to Supabase via the public anon key. All sensitive operations go
 * through SECURITY DEFINER RPC functions defined in migration 004.
 */

(function () {
  'use strict';

  // -------------------- Configuration --------------------

  const SUPABASE_URL = 'https://cskufwwwcytnhhjmtyya.supabase.co';
  const SUPABASE_ANON_KEY = 'sb_publishable_S-O4covVApNyWlILAyjGgw_XIRkVdkS';

  const COOKIE_NAME = 'as_ref';
  const COOKIE_DAYS = 90;
  const STORAGE_AUTH_KEY = 'as-referral-auth';

  // -------------------- Cookie helpers --------------------

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      ';expires=' +
      d.toUTCString() +
      ';path=/;samesite=lax';
  }

  function getCookie(name) {
    const parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  }

  function genCookieId() {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return Array.from(a)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // -------------------- Supabase REST helpers --------------------

  function rpc(fnName, body) {
    return fetch(SUPABASE_URL + '/rest/v1/rpc/' + fnName, {
      method: 'POST',
      headers: rpcHeaders(),
      body: JSON.stringify(body || {}),
    }).then(handleResponse);
  }

  function rpcHeaders() {
    const h = {
      apikey: SUPABASE_ANON_KEY,
      Authorization: 'Bearer ' + (getAccessToken() || SUPABASE_ANON_KEY),
      'Content-Type': 'application/json',
    };
    return h;
  }

  function handleResponse(resp) {
    return resp.text().then((text) => {
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = text;
      }
      if (!resp.ok) {
        const err = new Error(
          'Supabase ' + resp.status + ': ' + (data && data.message ? data.message : text)
        );
        err.status = resp.status;
        err.body = data;
        throw err;
      }
      return data;
    });
  }

  // -------------------- Auth (magic link) --------------------

  function loadAuth() {
    try {
      const raw = localStorage.getItem(STORAGE_AUTH_KEY);
      if (!raw) return null;
      const a = JSON.parse(raw);
      if (a && a.expires_at && Date.now() / 1000 < a.expires_at) return a;
      // expired — try refresh
      if (a && a.refresh_token) return a; // refresh handled lazily below
    } catch (e) {}
    return null;
  }

  function saveAuth(session) {
    if (!session) {
      localStorage.removeItem(STORAGE_AUTH_KEY);
      return;
    }
    localStorage.setItem(STORAGE_AUTH_KEY, JSON.stringify(session));
  }

  function getAccessToken() {
    const a = loadAuth();
    return a ? a.access_token : null;
  }

  function sendMagicLink(email, redirectTo) {
    return fetch(SUPABASE_URL + '/auth/v1/otp', {
      method: 'POST',
      headers: {
        apikey: SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      // create_user: true (default) — Supabase will create an auth.users row on first login.
      // Non-referrers who get an auth account still see "not_a_referrer" from the dashboard RPC,
      // so this is safe. The referrers table remains the gate for showing any data.
      body: JSON.stringify({
        email: email,
        options: { email_redirect_to: redirectTo || window.location.href },
      }),
    }).then(handleResponse);
  }

  function refreshSession(refreshToken) {
    return fetch(SUPABASE_URL + '/auth/v1/token?grant_type=refresh_token', {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).then(handleResponse);
  }

  function captureSessionFromHash() {
    const hash = window.location.hash || '';
    if (hash.indexOf('access_token=') === -1) return null;
    const params = new URLSearchParams(hash.slice(1));
    const session = {
      access_token: params.get('access_token'),
      refresh_token: params.get('refresh_token'),
      expires_in: parseInt(params.get('expires_in') || '3600', 10),
      expires_at: Math.floor(Date.now() / 1000) + parseInt(params.get('expires_in') || '3600', 10),
      token_type: params.get('token_type'),
    };
    if (!session.access_token) return null;
    saveAuth(session);
    // clean the URL
    history.replaceState({}, '', window.location.pathname + window.location.search);
    return session;
  }

  function logout() {
    saveAuth(null);
  }

  // -------------------- Public API --------------------

  const Referral = {
    // Sign-up flow
    checkEligibility: function (email) {
      return rpc('referral_check_eligibility', { p_email: email });
    },
    signup: function (email, phone) {
      return rpc('referral_signup', { p_email: email, p_phone: phone || null });
    },

    // Click tracking
    recordClick: function (code, opts) {
      opts = opts || {};
      let cookieId = getCookie(COOKIE_NAME + '_id');
      if (!cookieId) {
        cookieId = genCookieId();
        setCookie(COOKIE_NAME + '_id', cookieId, COOKIE_DAYS);
      }
      setCookie(COOKIE_NAME, code, COOKIE_DAYS);
      const params = new URLSearchParams(window.location.search);
      return rpc('referral_record_click', {
        p_code: code,
        p_cookie_id: cookieId,
        p_landing_page: window.location.href,
        p_referer: document.referrer || null,
        p_user_agent: navigator.userAgent || null,
        p_utm_source: params.get('utm_source'),
        p_utm_medium: params.get('utm_medium'),
        p_utm_campaign: params.get('utm_campaign'),
      });
    },

    // Cookie inspection
    getStoredReferralCode: function () {
      return getCookie(COOKIE_NAME);
    },
    getStoredCookieId: function () {
      return getCookie(COOKIE_NAME + '_id');
    },

    // Friend submitting their info via the referral link
    submitFriend: function (data) {
      return rpc('referral_submit_friend', {
        p_code: data.code || getCookie(COOKIE_NAME),
        p_friend_first_name: data.first_name,
        p_friend_last_name: data.last_name,
        p_friend_email: data.email || null,
        p_friend_phone: data.phone || null,
        p_cookie_id: getCookie(COOKIE_NAME + '_id'),
        p_notes: data.notes || null,
      });
    },

    // Auth + dashboard
    sendMagicLink: sendMagicLink,
    captureSessionFromHash: captureSessionFromHash,
    logout: logout,
    isLoggedIn: function () {
      return !!getAccessToken();
    },
    getMyDashboard: function () {
      return rpc('referral_get_my_dashboard', {});
    },
  };

  // -------------------- Site-wide cookie tracker --------------------
  // Auto-runs on every page load. If ?ref=CODE is present, drop a cookie.

  function autoTrack() {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('ref');
      if (!code) return;
      Referral.recordClick(code).catch(() => {
        /* swallow — tracking is best-effort */
      });
    } catch (e) {
      // swallow
    }
  }

  // -------------------- Boot --------------------

  // Capture auth from URL hash on dashboard return
  if (window.location.hash && window.location.hash.indexOf('access_token=') !== -1) {
    captureSessionFromHash();
  }

  // Auto-track ?ref= on every page
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    autoTrack();
  } else {
    document.addEventListener('DOMContentLoaded', autoTrack);
  }

  // expose
  window.ArcticReferral = Referral;
})();

